import React, { useState, useRef } from 'react';
import { Sparkles, Loader2, Play, Pause, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Curated OpenAI TTS female voices ordered from "best for FR hypnotic" to most
// English-leaning. Nova is the most reliable for French pronunciation.
const VOICE_OPTIONS = [
  { id: 'nova',    label: '🌙 Nova',    desc: 'Chaleureuse · meilleure pour le français' },
  { id: 'shimmer', label: '💫 Shimmer', desc: 'Douce, soufflée, sensuelle' },
  { id: 'coral',   label: '🌸 Coral',   desc: 'Claire, naturelle, vivante' },
  { id: 'sage',    label: '🌿 Sage',    desc: 'Calme, narrative, posée' },
  { id: 'fable',   label: '🎙 Fable',   desc: 'Raffinée, lente, contes' },
];

/**
 * Admin bulk-generator for OpenAI TTS "Féminine" voice clips. Exposes a voice
 * picker, a speed slider and a preview button so the admin can A/B test
 * voices on a single phrase before regenerating a whole theme (saves credits).
 *
 * The clips are stored as voice='sensuelle' in DB for backward compatibility
 * with existing 129+ clips; only the OpenAI voice/speed underneath changes.
 */
export default function SensualVoiceGenerator({ theme, onDone, visible = true, onVisibilityChange }) {
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [progress, setProgress] = useState(null);
  const [openaiVoice, setOpenaiVoice] = useState('nova');
  const [speed, setSpeed] = useState(0.9);
  const previewAudioRef = useRef(null);

  if (!theme || !Array.isArray(theme.affirmations) || theme.affirmations.length === 0) {
    return null;
  }

  const stopPreview = () => {
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch { /* noop */ }
      previewAudioRef.current = null;
    }
    setPreviewing(false);
  };

  const handlePreview = async () => {
    if (previewing) { stopPreview(); return; }
    setPreviewing(true);
    try {
      // Pick a representative affirmation (the first non-empty one)
      const sampleText = (theme.affirmations.find((t) => t && t.trim()) || '').slice(0, 200);
      const r = await axios.post(
        `${API}/api/affirmation-clips/preview-tts`,
        { text: sampleText, openai_voice: openaiVoice, speed },
        { responseType: 'blob', timeout: 60 * 1000 }
      );
      const url = URL.createObjectURL(r.data);
      const audio = new Audio(url);
      audio.volume = 1;
      audio.onended = () => { stopPreview(); URL.revokeObjectURL(url); };
      previewAudioRef.current = audio;
      await audio.play();
    } catch (e) {
      console.error('Preview failed', e);
      const msg = e?.response?.data?.detail || e.message || 'Erreur';
      toast.error(`Aperçu échoué : ${msg}`);
      stopPreview();
    }
  };

  const handleGenerate = async () => {
    const ok = window.confirm(
      `Générer les ${theme.affirmations.length} affirmations du thème « ${theme.label || theme.id} » avec la voix ${openaiVoice.toUpperCase()} (vitesse ${speed}) ?\n\nCela utilise des crédits OpenAI TTS (~0,30 € pour 34 affirmations) et remplacera la voix Féminine existante pour ce thème.`
    );
    if (!ok) return;
    stopPreview();
    setBusy(true);
    setProgress({ generated: 0, total: theme.affirmations.length });
    try {
      const r = await axios.post(
        `${API}/api/affirmation-clips/${theme.id}/generate-tts`,
        {
          affirmations: theme.affirmations,
          voice: 'sensuelle',
          openai_voice: openaiVoice,
          speed,
          model: 'tts-1-hd',
        },
        { timeout: 8 * 60 * 1000 } // 8 min — TTS generation is sequential
      );
      const d = r.data || {};
      setProgress({ generated: d.generated || 0, total: theme.affirmations.length, failed: d.failed || 0 });
      if (d.failed > 0) {
        toast.warning(`${d.generated} générées, ${d.failed} échouées`);
      } else {
        toast.success(`💕 ${d.generated} voix ${openaiVoice} générées pour « ${theme.label || theme.id} »`);
      }
      onDone && onDone();
    } catch (e) {
      console.error('TTS generation failed', e);
      const msg = e?.response?.data?.detail || e.message || 'Erreur inconnue';
      toast.error(`Génération échouée : ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full mt-2 rounded-2xl border border-pink-400/30 bg-pink-500/5 p-3 space-y-2"
      style={{ transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)', isolation: 'isolate' }}
      data-testid="sensual-voice-generator"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-pink-300" />
        <h4 className="text-pink-200 text-xs font-semibold flex-1">Voix Féminine (Admin)</h4>
        {/* Visibility toggle: show/hide the Féminine pill in the public user UI */}
        {onVisibilityChange && (
          <button
            type="button"
            onClick={() => onVisibilityChange(!visible)}
            className={`text-[9px] px-2 py-0.5 rounded-full border transition flex items-center gap-1 ${visible ? 'bg-emerald-500/20 border-emerald-400/50 text-emerald-200' : 'bg-slate-800 border-white/15 text-white/55'}`}
            data-testid="feminine-visibility-toggle"
            title={visible ? 'Dans l\'interface utilisateur · clic pour retirer' : 'Hors interface utilisateur · clic pour mettre dans l\'UI'}
          >
            {visible ? '👁 Dans l\'UI' : '🚫 Hors UI'}
          </button>
        )}
      </div>
      {!visible && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-200 leading-snug">
          {'⚠️ La voix Féminine est '}<b>{'réservée à l\'admin'}</b>{'. Elle n\'apparaît pas dans le sélecteur de voix des utilisateurs. Cliquez sur '}<b>{'🚫 Hors UI'}</b>{' ci-dessus pour la mettre dans l\'interface utilisateur. Les clips OpenAI pourront aussi être remplacés plus tard par des enregistrements humains.'}
        </div>
      )}
      <p className="text-white/55 text-[10px] leading-snug">
        Choisissez une voix puis testez-la sur 1 phrase avant de régénérer tout le thème.
        Pour un français fiable et hypnotique, <b>Nova</b> à vitesse <b>0.9</b> est recommandé.
      </p>

      {/* Voice picker */}
      <div className="grid grid-cols-1 gap-1.5" data-testid="voice-picker">
        {VOICE_OPTIONS.map((v) => {
          const active = openaiVoice === v.id;
          return (
            <button
              key={v.id}
              onClick={() => { setOpenaiVoice(v.id); stopPreview(); }}
              disabled={busy}
              className={`text-left px-2.5 py-1.5 rounded-lg border transition flex items-center justify-between ${active ? 'bg-pink-500/25 border-pink-400/60' : 'bg-slate-900/70 border-white/10 hover:bg-slate-800'}`}
              data-testid={`voice-option-${v.id}`}
            >
              <div>
                <p className={`text-[11px] font-medium ${active ? 'text-pink-100' : 'text-white/85'}`}>{v.label}</p>
                <p className="text-white/45 text-[9px]">{v.desc}</p>
              </div>
              <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${active ? 'border-pink-300 bg-pink-300' : 'border-white/25'}`} />
            </button>
          );
        })}
      </div>

      {/* Speed slider */}
      <div className="flex items-center gap-2 pt-1" data-testid="speed-slider-row">
        <span className="text-white/60 text-[10px] flex-shrink-0">Vitesse</span>
        <input
          type="range" min="0.7" max="1.2" step="0.05"
          value={speed}
          onChange={(e) => setSpeed(parseFloat(e.target.value))}
          disabled={busy}
          className="flex-1 accent-pink-400"
          data-testid="speed-slider"
        />
        <span className="text-pink-200 text-[10px] font-mono w-8 text-right">{speed.toFixed(2)}</span>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <button
          onClick={handlePreview}
          disabled={busy}
          data-testid="preview-tts-btn"
          className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/15 bg-slate-900 text-white/80 text-[11px] font-medium hover:bg-slate-800 disabled:opacity-50 transition"
        >
          {previewing ? (
            <>
              <Pause size={11} /> Stop aperçu
            </>
          ) : (
            <>
              <Play size={11} /> 🔊 Tester
            </>
          )}
        </button>
        <button
          onClick={handleGenerate}
          disabled={busy}
          data-testid="generate-sensual-voice-btn"
          className="flex-1 min-w-0 inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-pink-400/40 bg-pink-500/20 text-pink-100 text-[11px] font-semibold hover:bg-pink-500/30 disabled:opacity-50 disabled:cursor-wait transition"
        >
          {busy ? (
            <>
              <Loader2 size={11} className="animate-spin" />
              Génération…
            </>
          ) : (
            <>
              <RefreshCw size={11} />
              Régénérer tout le thème
            </>
          )}
        </button>
      </div>
      {progress && !busy && (
        <p className="text-[9px] text-white/50 text-center">
          ✓ {progress.generated}/{progress.total} générées
          {progress.failed > 0 ? ` · ${progress.failed} échouées` : ''}
        </p>
      )}
    </div>
  );
}
